/**
 * @fileoverview Consent helpers shared by the roster, profile, and recording pages.
 *
 * Mirrors the mobile app's consent semantics: students carry an `age_band`
 * (adult | 13_17 | under_13) and a `consent_status` (none | requested |
 * granted | declined | revoked). Recording is allowed only when status is
 * exactly "granted" — legacy students with no consent fields are blocked.
 */

import { isProgramRosterStudent, realStudentEmail } from "@/lib/importedStudents";

export const AGE_BAND_OPTIONS = [
  { value: "adult", label: "Adult" },
  { value: "13_17", label: "13–17" },
  { value: "under_13", label: "Under 13" },
];

/**
 * Display label for a student's age band (e.g. "Adult", "13–17", "Under 13").
 * Returns null for legacy students with no age band set.
 */
export const ageBandLabel = (band) =>
  AGE_BAND_OPTIONS.find((o) => o.value === band)?.label || null;

export const MINOR_AGE_BANDS = ["13_17", "under_13"];

export const isMinorBand = (band) => MINOR_AGE_BANDS.includes(band);

export const PARENT_EMAIL_REQUIRED_MESSAGE =
  "Students under 18 need a parent or guardian email so the consent request can be sent.";

/**
 * A program's roster student (see `isProgramRosterStudent`) gets consent from
 * the organization that enrolled them, never from a request the instructor
 * sends. Where such a student has no answer yet, the tag reads as this label,
 * in the cool colour, instead of the red "Not requested": nothing is wrong
 * that the instructor could fix.
 */
export const PROGRAM_ROSTER_LABEL = "Program roster";

export const PROGRAM_ROSTER_CONSENT_NOTE = "Consent for program roster students comes from the organization.";

/** The consent states that carry an answer of their own, and keep their tag. */
const ANSWERED_STATES = ["granted", "requested", "declined", "revoked"];

/**
 * Whether this student's consent tag reads "Program roster": a program's
 * roster student with no request on record. Granted, requested, declined and
 * revoked keep their own labels and colours for everyone.
 */
export const showsProgramRosterConsent = (student) =>
  isProgramRosterStudent(student) && !ANSWERED_STATES.includes(student?.consent_status);

/**
 * Helper copy shown under the age-group field, naming who the consent request
 * goes to (parent for minors, the student themselves for adults). Requests are
 * never auto-sent — the instructor sends one from the student's profile.
 */
export const consentAudienceHint = (band) =>
  isMinorBand(band)
    ? "Recording stays locked until consent is granted. You can send the request to the parent or guardian from the student's profile."
    : "Recording stays locked until consent is granted. You can send the request to the student from their profile.";

/**
 * Status hint shown next to the consent badge on the student profile.
 * Returns null when consent is granted (no hint needed).
 */
export const consentStatusHint = (student) => {
  const status = student?.consent_status;
  if (status === "granted") return null;
  // Nothing here is the instructor's to send, so no hint may offer it.
  if (isProgramRosterStudent(student)) {
    const lead =
      status === "revoked"
        ? "Consent was withdrawn and stored transcripts were deleted."
        : status === "declined"
          ? "Consent was declined."
          : "Recording is locked until consent is granted.";
    return `${lead} ${PROGRAM_ROSTER_CONSENT_NOTE}`;
  }
  if (status === "revoked")
    return "Consent was withdrawn and stored transcripts were deleted. You can send a new request — recording unlocks again if it’s accepted.";
  if (status === "declined")
    return "The consent request was declined. You can send a new request after talking with them.";
  if (!student?.age_band)
    return "Set the student’s age group (edit their profile) to send a consent request.";
  if (status === "requested")
    return "Recording is locked until the consent form is accepted.";
  return "Recording is locked. Send a consent request to unlock it.";
};

/**
 * Interpret a failure from the lesson upload flow. The consent gate on
 * POST /lessons/start-upload returns a 403 whose detail is a structured
 * object ({code: "consent_required", message, pending_students}) rather
 * than the usual string — retrying cannot help, so the recording flow
 * should discard the capture and return to the select stage.
 * @returns {{consentBlocked: boolean, message: string|null}}
 */
export const resolveUploadError = (error) => {
  const detail = error.response?.data?.detail;
  if (detail && typeof detail === "object" && detail.code === "consent_required") {
    const base = detail.message || "Recording is locked until consent is granted.";
    const names = (detail.pending_students || []).filter(Boolean).join(", ");
    return { consentBlocked: true, message: names ? `${base} Awaiting consent: ${names}` : base };
  }
  return { consentBlocked: false, message: null };
};

/**
 * Whether the resend/request-consent action applies. Hidden only once granted.
 * Declined and revoked students may both be re-asked — the backend rotates a
 * fresh token and flips the status back to "requested" (revoking already
 * deleted the audio; re-requesting does not restore it, it just re-opens the
 * gate if the new request is accepted).
 *
 * Never for a program's roster student: their consent is the organization's
 * to collect, and the server refuses the request (403
 * `program_roster_consent`).
 */
export const canRequestConsent = (student) =>
  student?.consent_status !== "granted" && !isProgramRosterStudent(student);

/**
 * Whether the "mark as consented (offline)" action applies. Like
 * {@link canRequestConsent} it is hidden once granted, but it also requires an
 * age band because the backend refuses to record consent without one (it drives
 * the audience and the under-13 COPPA path).
 */
export const canMarkOfflineConsent = (student) =>
  student?.consent_status !== "granted" && !!student?.age_band;

/**
 * Whether this family holds a live grant made under consent text Hans has
 * since superseded, so the instructor can record that they confirmed the
 * current terms individually.
 *
 * Reads the server-resolved `consent_update_available` rather than comparing
 * versions here: the backend parser fails closed by design, and a second
 * implementation would eventually disagree with it about the same student.
 * An older payload simply omits the field, which reads as "nothing to do".
 *
 * Deliberately separate from {@link canMarkOfflineConsent} rather than a
 * loosening of it. That helper answers "this student has no consent" and is
 * read inside the recording-blocked banners; a granted student is not blocked
 * from recording, so widening it would put an update prompt in a banner about
 * a problem this family does not have.
 *
 * Unchanged for a program's roster student, like `canMarkOfflineConsent`: it
 * sends nothing, it records the instructor's own attestation.
 */
export const canUpdateConsent = (student) =>
  student?.consent_update_available === true && !!student?.age_band;

/**
 * Copy for the offline-consent confirmation popup explaining the COPPA
 * email-plus fraud-check. For an under-13 student the parent is emailed a
 * confirmation with a cancel link *if* a parent email is on file — the manual
 * grant otherwise has no first-party parental signal at all. Returns null for
 * adults and teens (no email-plus step).
 */
export const offlineConsentEmailPlusNotice = (student) => {
  if (student?.age_band !== "under_13") return null;
  return student?.parent_email
    ? `Because this student is under 13, a confirmation email will be sent to the parent (${student.parent_email}) so they can cancel if they didn’t authorize this.`
    : "No parent email is on file, so no confirmation email can be sent. Add a parent email first if you want the parent notified.";
};

/**
 * Who a one-to-one lesson summary is emailed to, by age band.
 *
 * A mirror of the backend's `_summary_recipients` (routes/email.py), which is
 * the authority: an under-13 student's summary goes to the parent ONLY; a
 * 13–17 student's goes to the parent, plus the teen only when the parent
 * opted in (`cc_teen`); an adult (or a legacy student with no band) gets it
 * directly, plus any extra contact (`parent_email`) on file. Placeholder
 * import addresses never count.
 *
 * Returns `[{ role, email }]` in send order, where `role` is "student",
 * "guardian" (a minor's parent) or "contact" (an adult's extra contact).
 * Used to describe recipients before a send; after one, prefer the
 * `sent_to` list the send-email endpoint returns.
 */
export const summaryRecipients = (student) => {
  const band = student?.age_band;
  const own = realStudentEmail(student?.email);
  const parent = realStudentEmail(student?.parent_email);
  const out = [];
  if (band === "under_13") {
    if (parent) out.push({ role: "guardian", email: parent });
  } else if (band === "13_17") {
    if (parent) out.push({ role: "guardian", email: parent });
    if (student?.cc_teen && own) out.push({ role: "student", email: own });
  } else {
    if (own) out.push({ role: "student", email: own });
    if (parent) out.push({ role: "contact", email: parent });
  }
  return out;
};

/** "a", "a and b", "a, b and c" — for a "Sent to …" confirmation. */
export const formatRecipientList = (emails) => {
  const list = (emails || []).filter(Boolean);
  if (list.length <= 1) return list[0] || "";
  return `${list.slice(0, -1).join(", ")} and ${list[list.length - 1]}`;
};

/**
 * The consent state in two or three words, for a line that names the
 * student (the recorder's who-card and picker, iOS review B6). "Consent on
 * file" once granted, where the line says so itself.
 *
 * @param {Object|null} student
 * @returns {string}
 */
export const consentStateLabel = (student) => {
  const status = student?.consent_status;
  if (status === "granted") return "Consent on file";
  if (isProgramRosterStudent(student)) return `${PROGRAM_ROSTER_LABEL} · consent pending`;
  if (status === "requested") return "Consent requested";
  if (status === "declined") return "Consent declined";
  if (status === "revoked") return "Consent withdrawn";
  return "Consent not on file";
};
